import Parse from "parse"
import { ALERTS_PATHS } from "../../utils/alertsUtils"
import { actionWithLoader, onEnter, push } from "../Utils/utils"
import { getCookingDelaysPSEsSelector, getCookingDelaysReloadedPSEsSelector, getQHSEConformityPSEsSelector, getQHSEConformityReloadedPSEsSelector } from "../../reducers/Alerts/alerts"
import { updateReproductionNeed } from "./Reproduction"
import { openAppMessageSnackBar } from "../Utils/app"
import { formatDateUrl } from "../../utils"

export const setQHSEConformityLoadingSlice = (loading) => {
    return (dispatch) => {
        dispatch({
            type: "QHSE_CONFORMITY_LOADING",
            loading
        })
    }
}

export const setCookingDelaysLoadingSlice = (loading) => {
    return (dispatch) => {
        dispatch({
            type: "COOKING_DELAYS_LOADING",
            loading
        })
    }
}

export const setReproductionNeedsLoadingSlice = (loading) => {
    return (dispatch) => {
        dispatch({
            type: "REPRODUCTION_NEEDS_LOADING",
            loading
        })
    }
}

/**
 * Load all alerts in page reload
 * So it is used only once when the user enters the alerts page
 * @returns 
 */
export const loadQHSEConformityAlerts = (dateStr) => {
  return async (dispatch) => {
    dispatch(setQHSEConformityLoadingSlice(true))
    
    const productionStepExecutions = await Parse.Cloud.run("getQHSEProductionStepExecutions", { dateStr })
    dispatch({
      type: "QHSE_PSES_LOADED",
      productionStepExecutions
    })
    dispatch(setQHSEConformityLoadingSlice(false))
  }
}

/**
 * This function is used to reload automatically the QHSE conformity alerts.
 * It fetches the latest production step executions from the server everty 5 minutes
 */
export const getQHSEConformityAlertsToReload = (dateStr) => {
  // @NOTE: we do not need a loader here because this action is called behind the scene
  return async (dispatch, getState) => {
    const state = getState()
    const previousProductionStepExecutions = getQHSEConformityPSEsSelector(state)
    const productionStepExecutions = await Parse.Cloud.run("getQHSEProductionStepExecutions", { dateStr })

    const reloadedProductionStepExecutions = []

    for (const productionStepExecution of productionStepExecutions) {
      const existingPSE = previousProductionStepExecutions.find(pse => pse.objectId === productionStepExecution.objectId)
      if (!existingPSE) {
        reloadedProductionStepExecutions.push(productionStepExecution)
      }
    }

    dispatch({
      type: "QHSE_PSES_RELOADED",
      productionStepExecutions: reloadedProductionStepExecutions
    })
  }
}

/**
 * This function adds a production step execution to the list of reloaded QHSE conformity alerts.
 * It is used when a user manually reloads the alerts (via a button click).
 * @returns 
 */
export const addReloadedQHSEConformityAlerts = () => {
  return actionWithLoader(async (dispatch, getState) => {
    const state = getState()
    const newProductionStepExecutions = getQHSEConformityReloadedPSEsSelector(state)
    const previousProductionStepExecutions = getQHSEConformityPSEsSelector(state)
    const newReloadedPSEs = [...newProductionStepExecutions, ...previousProductionStepExecutions]

    dispatch({
      type: "QHSE_PSES_LOADED",
      productionStepExecutions: newReloadedPSEs,
    })

    // clear the reloaded PSEs after adding them to the main list
    dispatch({
      type: "QHSE_PSES_RELOADED",
      productionStepExecutions: [],
    })
  })
}

export const loadCookingDelays = (dateStr) => {
  return async (dispatch) => {
    dispatch(setCookingDelaysLoadingSlice(true))
    const productionStepExecutions = await Parse.Cloud.run("getCookingDelaysPSEs", { dateStr })
    dispatch(setCookingDelaysLoadingSlice(false))

    dispatch({
      type: "COOKING_DELAYS_PSES_LOADED",
      productionStepExecutions
    })
  }
}


export const cancelReproduction = ({ reprodId }) => {
  return actionWithLoader(async dispatch => {
    try {
      await Parse.Cloud.run("cancelReproduction", { reproductionId: reprodId })

      dispatch(updateReproductionNeed(reprodId, "CANCELLED"))
      dispatch(openAppMessageSnackBar("La reproduction a été annulée avec succès."))

      return true
    } catch (error) {
      dispatch(openAppMessageSnackBar("Une erreur est survenue", "error"))

      return false
    }
  })
}

/**
 * Get the cooking delays alerts
 * Add to the notification for the new ones
 * @NOTE it will not update the displayed list yet, just the notification count
 * @returns 
 */
export const getCookingDelaysAlertsToReload = (dateStr) => {
  // @NOTE: we do not need a loader here because this action is called behind the scene
  return async (dispatch, getState) => {
    const state = getState()
    const previousPSEsBySections = getCookingDelaysPSEsSelector(state)
    const reloadedPSEsBySections = await Parse.Cloud.run("getCookingDelaysPSEs", { dateStr })

    let notificationCount = 0
    const reloadedProductionStepExecutionsBySections = []

    for (const reloadedPSEsBySection of reloadedPSEsBySections) {
      const prevSection = previousPSEsBySections.find(pse => pse.section.objectId === reloadedPSEsBySection.section.objectId)
      if (prevSection) {
        const reloadedPSEs = reloadedPSEsBySection.groupedProductionStepExecutions || []
        const newReloadedPSEs = []

        const prevPSEs = prevSection.groupedProductionStepExecutions || []

        for (const reloadedPSE of reloadedPSEs) {
          const existingPSE = prevPSEs.find(pse => pse.objectId === reloadedPSE.objectId)
          if (!existingPSE) {
            reloadedPSE.isNew = true // Not used for now
            reloadedPSEsBySection.withNewPSEs = true // Not used for now
            notificationCount = notificationCount + 1
          }
          newReloadedPSEs.push(reloadedPSE)
        }
        reloadedProductionStepExecutionsBySections.push({
          ...reloadedPSEsBySection,
          groupedProductionStepExecutions: newReloadedPSEs
        })
      } else {
        reloadedPSEsBySection.withNewPSEs = true // Not used for now
        notificationCount = notificationCount + (reloadedPSEsBySection.groupedProductionStepExecutions?.length || 0)
        reloadedProductionStepExecutionsBySections.push(reloadedPSEsBySection)
      }
    }

    dispatch({
      type: "COOKING_DELAYS_PSES_RELOADED",
      productionStepExecutions: reloadedProductionStepExecutionsBySections,
      cookingDelaysNotificationCount: notificationCount
    })
  }
}

/**
 * Update the displayed cooking delays alerts
 * And clear the notification count
 * @returns 
 */
export const addReloadedCookingDelaysAlerts = () => {
  return async (dispatch, getState) => {
    const state = getState()
    dispatch(setCookingDelaysLoadingSlice(true))
    const newPSEsBySection = getCookingDelaysReloadedPSEsSelector(state)
    dispatch(setCookingDelaysLoadingSlice(false))

    dispatch({
      type: "COOKING_DELAYS_PSES_LOADED",
      productionStepExecutions: newPSEsBySection,
    })

    // clear the reloaded PSEs after adding them to the main list
    dispatch({
      type: "COOKING_DELAYS_PSES_RELOADED",
      reloadedCookingDelaysPSEs: [],
      cookingDelaysNotificationCount: 0
    })
  }
}

/**
 * Load the reproductions and related PSEs from the backend.
 * Currently used to fetch reprods for the "Alertes" page when the user selects the "Reprod" tab.
 */
export const getReproductionNeeds = (dateStr) =>
  actionWithLoader(async (dispatch) => {
    dispatch(setReproductionNeedsLoadingSlice(true))
    try {
      const productionStepExecutions = await Parse.Cloud.run("getReproductionNeeds", { dateStr })
      dispatch({
        type: "REPRODUCTION_NEEDS_PSES_LOADED",
        productionStepExecutions: productionStepExecutions || [],
      })
    } catch (e) {
      dispatch(openAppMessageSnackBar("Une erreur est survenue lors du chargement des reprods.", "error"))
    } finally {
      dispatch(setReproductionNeedsLoadingSlice(false))
    }
  })

/**
 * Load alerts based on the current tab
 * This function is called when the user enters the alerts page.
 * @param {*} params 
 * @returns 
 */
export const loadAlerts = (params) => {
  return (dispatch) => {
    const { tab, date } = params || {}
    const formattedDate = date ? formatDateUrl(date, true) : null
  
    switch (tab) {
      case ALERTS_PATHS.cookingDelays:
        dispatch(loadCookingDelays(formattedDate))
        break
      case ALERTS_PATHS.conditioningDelays:
        // Load conditioning delays alerts
        break
      case ALERTS_PATHS.reproductionNeeds:
        dispatch(getReproductionNeeds(formattedDate))
        break
      case ALERTS_PATHS.qhseConformity:
        dispatch(loadQHSEConformityAlerts(formattedDate))
        break
      default:
        dispatch(loadQHSEConformityAlerts(formattedDate))
        break
    }
  }
}

export const onEnterAlerts = (store) => {
  return onEnter({
    store,
    actionThunk: loadAlerts
  })
}

export const showAlerts = (date, tab = ALERTS_PATHS.qhseConformity) => {
  let root = "/" + ALERTS_PATHS.root

  if (tab) {
    root += `/${tab}`
  }

  if (date) {
    root += `/${date}`
  }

  return push(root)
}

export const showAlertsFilter = () => {
  return push(`/${ALERTS_PATHS.root}/filter`)
}
